import React, { useState, useEffect } from "react";

import ManagedTextArea from "input/ManagedTextArea";
import ManagedInput from "input/ManagedInput";

import * as RD from "reports/ReportDef";

interface ReportGoalManagerProps {
    goals: RD.Goals;
    originalGoals: RD.Goals;
    showRestoreButton?: boolean;
    /** The function from the parent's state hook to update the latest value (e.g., the 'update' function) */
    updateGoals: (newValueOrFn: React.SetStateAction<RD.Goals>) => void;
}

const ReportGoalsManager: React.FC<ReportGoalManagerProps> = ({
    goals,
    originalGoals,
    showRestoreButton,
    updateGoals
}) => {

    const [displayGoals, setDisplayGoals] = useState<RD.Goals>([]);

    useEffect(
        () => {
            setDisplayGoals(Array.isArray(goals) ? goals : []);
        },
        [goals]
    );

    const handleAddGoal = () => {
        setDisplayGoals([...displayGoals, { goal: "", detail: "" }]);
        updateGoals(prevGoals => [...prevGoals, { goal: "", detail: "" }]);
    };

    const handleDeleteGoal = (index: number) => {
        setDisplayGoals(displayGoals.filter((_, i) => i !== index));
        updateGoals(prevGoals => prevGoals.filter((_, i) => i !== index));
    };

    const handleDeleteAllGoals = () => {
        setDisplayGoals([]);
        updateGoals([]);
    };

    const handleGoalInputChange = (index: number, field: keyof RD.Goal, value: string) => {
        setDisplayGoals(displayGoals.map((goal, i) => i === index ? { ...goal, [field]: value } : goal))
        updateGoals(prevGoals => prevGoals.map((goal, i) => i === index ? { ...goal, [field]: value } : goal));
    };

    const handleRevertToInitialGoals = () => {
        setDisplayGoals(originalGoals);
        updateGoals(originalGoals);
    };

    const shouldShowRestore = typeof showRestoreButton === "boolean" ? showRestoreButton : (Array.isArray(goals) && goals.length > 0);

    return (
        <div id="patient-goals" className="w-[80%] mx-auto">
            <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r rounded-t-md shadow-sm mt-4 first:mt-0">
                Patient Goals
            </div>
            <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm"> {/* Changed bg-gray-25 to bg-gray-50 as -25 is not standard */}
                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                    Outline the real-life goals the patient set for themselves at the start of the program.
                </div>
                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                    {
                        displayGoals.length > 0
                            ? (
                                <div className="w-full space-y-4">
                                    {
                                        displayGoals.map((goal: RD.Goal, index: number) => (
                                            <div key={`goal-item-${index}`}>
                                                <div className="mb-3">
                                                    <span className="inline-flex gap-x-1 pb-2">
                                                        <p className="text-gray-700 text-sm font-medium leading-tight">Goal {index + 1}</p>
                                                        <button className="cursor-pointer" type="button" onClick={() => handleDeleteGoal(index)}>
                                                            <img className="h-4 w-4 " src={"/icons/trash.svg"} alt="icon"></img>
                                                        </button>
                                                    </span>
                                                    <ManagedInput
                                                        id={`goal-title-${index}`}
                                                        className="w-full p-2 border bg-white border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        placeholder="Title (1-6 words)"
                                                        type="text"
                                                        // useDirtyState props
                                                        value={goal.goal ?? ""}
                                                        updateValue={(goal: string) => handleGoalInputChange(index, "goal", goal)}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <ManagedTextArea
                                                        id={`goal-description-${index}`}
                                                        className="w-full p-2 border bg-white border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        placeholder="Description (Progress and Action Steps)"
                                                        rows={3}
                                                        // useDirtyState props
                                                        value={goal.detail ?? ""}
                                                        updateValue={(detail: string) => handleGoalInputChange(index, "detail", detail)}
                                                    />
                                                </div>
                                            </div>
                                        ))
                                    }
                                    {
                                        shouldShowRestore && (
                                            <div>
                                                <span className="text-gray-600 flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        className="flex-shrink-0 flex relative justify-center items-center h-7 px-0.5 hover:bg-gray-300 border-gray-300 bg-white rounded-lg shadow border text-sm text-gray-700 font-medium transition-colors duration-150 cursor-pointer"
                                                        onClick={(e) => { e.preventDefault(); handleRevertToInitialGoals(); }}
                                                    >
                                                        <img className="w-6 h-4" src="/icons/flip-backward.svg" alt="icon"/>
                                                    </button>
                                                    <span className="ml-1.5 whitespace-nowrap">
                                                        Restore Unedited Goals
                                                    </span>
                                                </span>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                            : (
                                <span className="self-stretch text-warning-600 font-semibold leading-7">&#9888; No Goals found in Practice Better data. You can add them manually below.</span>
                            )
                    }
                </div>
                <div className="flex flex-col sm:flex-row self-stretch w-full justify-center items-center gap-3">
                    <button
                        type="button"
                        className="w-fit px-4 py-2.5 bg-white hover:bg-gray-300 rounded-lg shadow border border-gray-300 justify-center items-center gap-1.5 inline-flex cursor-pointer"
                        onClick={e => {e.preventDefault(); handleAddGoal();}}
                    >
                        <div className="px-0.5 justify-center gap-x-2 items-center inline-flex">
                            <img className="w-5 h-5 pl-[3.33px] justify-center items-center inline-flex" src="/icons/plus.svg" alt="icon"/>
                            <div className="text-gray-700 text-base font-semibold leading-normal">
                                Add New Goal
                            </div>
                        </div>
                    </button>
                </div>
            </div>
            <div className="px-7 pb-4 bg-gray-50 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
        </div>
    );
};

// ---=== Utilities ===--- //

const stringContainsName = (str: string, name: string): boolean => {
    return name.split(" ").some(part => str.toLowerCase().includes(part.toLowerCase()));
};

const cleanGoalInput = (text: string) => {
    return `${text}${text.endsWith(".") ? "" : "."}`;
};

export const parseSampledGoals = (sampledGoals: string[][]): RD.Goals => {
    const parsedGoals: RD.Goals = [];

    for (const goalData of sampledGoals) {
        switch (goalData.length) {
            case 0:
                parsedGoals.push({ goal: "", detail: "" } as RD.Goal);
                break;
            case 1:
                parsedGoals.push({ goal: goalData[0], detail: "" } as RD.Goal);
                break;
            case 2:
                parsedGoals.push({ goal: goalData[0], detail: goalData[1] ?? "" } as RD.Goal);
                break;
            default:
                console.error(`parseSampledGoals: Invalid number of goal data elements: [${goalData.join(", ")}]`);
                break;
        }
    }

    return parsedGoals;
};

export const goalsContainName = (goals: RD.Goals, name: string) => {
    return goals.some((goal: RD.Goal) => {return stringContainsName(goal.goal, name) || stringContainsName(goal.detail, name);});
};

export const validateGoals = (goals: RD.Goals): [RD.Goals, string[]] => {
    let validatedGoals: RD.Goals = [];
    let errors: string[] = [];

    goals.forEach(
        (goal: RD.Goal, i: number) => {
            const cleanGoal = goal.goal.trim();
            const cleanDetail = goal.detail.trim();
            (cleanGoal && cleanDetail)
                ? validatedGoals.push({ goal: cleanGoal, detail: cleanGoalInput(cleanDetail) } as RD.Goal)
                : errors.push(`Subjective Outcomes: Incomplete Goal ${i+1}. Please remove or complete.`);
        }
    );

    return [validatedGoals, errors];
};

export default ReportGoalsManager;